import {createActions} from 'redux-actions';
import fetchJson, {
	postJson,
	putJson,
	deleteJson,
	postForm,
	fetchNonCachedJson
} from '../../fetchJson';
import {push} from 'connected-react-router';
import handleError from '../../handleError';
import {uiActions} from '../../chrome';
import RedeemCoupon from './redeem-coupon/RedeemCoupon';
import {profileCouponActions} from '../../profile';

const actions = createActions(
	'COUPON_ADDING',
	'COUPON_DESC_ADDING',
	'COUPON_CODE_ADDING',
	'COUPON_EXPIRY_ADDING',
	'COUPON_QUANTITY_ADDING',
	'COUPON_PRODUCT_LINK_ADDING',
	'COUPON_ADDED',
	'COUPON_ADDING_CANCELED',
	'COUPON_LISTING',
	'COUPON_PENDING_APPROVAL_LISTING',
	'COUPON_APPROVED',
	'COUPON_REJECTED',
	'COUPON_DELETED',
	'COUPON_REDEEMING',
	'COUPON_REDEEMED',
	'COUPON_REDEEM_FAILED',
	'COUPON_EDITING',
	'COUPON_DESC_EDITING',
	'COUPON_EXPIRY_EDITING',
	'COUPON_QUANTITY_EDITING',
	'COUPON_PRODUCT_LINK_EDITING',
	'COUPON_UPDATED',
	'COMMUNITY_COUPON_UPDATED',
	'PENDING_COUPON_UPDATED',
	'SET_COUPON_STATISTICS',
	'CLEAR_COUPON_STATISTICS'
);

export const persistExistingCoupon = () => (dispatch, getState) => {
	const coupon = getState().coupon.adding;

	const couponData = {
		communityUuid: coupon.communityUuid,
		description: coupon.description,
		code: coupon.code,
		expiryDate: coupon.expiryDate,
		productLink: coupon.productLink,
		userUuid: coupon.userUuid,
		couponUuid: coupon.couponUuid,
		quantity: coupon.quantity
	};

	postJson(`/api/coupon`, couponData)
		.then(newCoupon => {
			dispatch(actions.couponAdded({newCoupon}));
			dispatch(profileCouponActions.profileCouponAdded({newCoupon}));
			dispatch(uiActions.uiMessage(`Coupon has been created and sent to admin for approval`));
		})
		.catch(error => {
			handleError(dispatch);
		});
};

/**
 * Update coupon action
 * @returns
 */
export const persistEditedCoupon = () => (dispatch, getState) => {
	const coupon = getState().coupon.editing;
	const session = getState().session.current;
	const section = getState().coupon.couponEditSection;

	let userData;

	if (session && session.user) {
		userData = {
			uuid: session.user.uuid
		};
	}

	let couponData = {
		description: coupon.description,
		expiryDate: coupon.expiryDate,
		quantity: coupon.quantity,
		productLink: coupon.productLink
	};

	putJson(`/api/coupon/update/${coupon.uuid}`, {couponData: couponData, user: userData})
		.then(updatedCoupon => {
			if (section === 'COMMUNITY_APPROVED_COUPONS') {
				dispatch(actions.communityCouponUpdated(updatedCoupon.coupon));
			} else if (section === 'COMMUNITY_PENDING_APPROVAL_COUPONS') {
				dispatch(actions.pendingCouponUpdated(updatedCoupon.coupon));
			} else if (section === 'PROFILE_COUPON_PENDING_APPROVAL') {
				dispatch(profileCouponActions.profileCouponUpdated(updatedCoupon.coupon));
			} else {
				dispatch(profileCouponActions.profileCouponUpdated(updatedCoupon.coupon));
			}

			dispatch(uiActions.uiMessage(`Coupon has been updated successfully.`));
			dispatch(uiActions.uiModal(null));
		})
		.catch(handleError(dispatch));
};

/**
 * API call to fetch approved coupons list
 */
export const fetchApprovedCoupons = data => (dispatch, getState) => {
	let userUuid = null;

	if (getState().session && getState().session.current && getState().session.current.user) {
		userUuid = getState().session.current.user.uuid;
	}

	let queryString = `/api/coupons/?communityUuid=${data.communityUuid}&status=approved`;

	if (userUuid) {
		queryString = queryString + `&userUuid=${userUuid}`;
	}

	fetchJson(queryString)
		.then(coupons => dispatch(actions.couponListing(coupons)))
		.catch(handleError(dispatch));
};

/**
 * API call to fetch coupons waiting for approval
 */
export const fetchCouponsPendingApproval = data => (dispatch, getState) => {
	let userUuid;

	if (getState().session && getState().session.current && getState().session.current.user) {
		userUuid = getState().session.current.user.uuid;
	}

	let queryString = `/api/coupons/?communityUuid=${data.communityUuid}&status=unapproved`;

	if (userUuid) {
		queryString = queryString + `&userUuid=${userUuid}`;
	}

	fetchNonCachedJson(queryString)
		.then(couponsPendingApproval =>
			dispatch(actions.couponPendingApprovalListing(couponsPendingApproval))
		)
		.catch(handleError(dispatch));
};

export const rejectCoupon = couponUuid => (dispatch, getState) => {
	const rejectorUuid = getState().session.current.user.uuid;

	putJson(`/api/coupon/${couponUuid}/reject`, {rejectorUuid: rejectorUuid})
		.then(approvedCoupon => {
			dispatch(actions.couponRejected(couponUuid));
		})
		.catch(handleError(dispatch));
};

export const approveCoupon = couponUuid => (dispatch, getState) => {
	const approverUuid = getState().session.current.user.uuid;

	putJson(`/api/coupon/${couponUuid}/approve`, {approverUuid: approverUuid})
		.then(rejectedCoupon => {
			dispatch(actions.couponApproved(couponUuid));
		})
		.catch(handleError(dispatch));
};

export const deleteCoupon = (couponUuid, couponStatus) => (dispatch, getState) => {
	const deletedBy = getState().session.current.user.uuid;

	putJson(`/api/coupon/${couponUuid}`, {deletedBy: deletedBy})
		.then(rejectedCoupon => {
			dispatch(actions.couponDeleted({couponUuid: couponUuid, couponStatus: couponStatus}));
		})
		.then(data => {
			dispatch(uiActions.uiMessage(`Coupon has been deleted successfully`));
		})
		.catch(handleError(dispatch));
};

/**
 * Function to redeem coupon
 * @param {*} coupon
 * @returns
 */
export const redeemCoupon = coupon => (dispatch, getState) => {
	const userUuid = getState().session.current.user.uuid;

	// emit action to show loader when coupon is being redeemed
	dispatch(actions.couponRedeeming({coupon}));

	postJson(`/api/coupon/redeem/${coupon.uuid}`, {userUuid: userUuid})
		.then(response => {
			if ('error' in response.resData) {
				dispatch(actions.couponRedeemFailed({coupon}));
				dispatch(
					uiActions.uiMessage({
						content: () => 'Could not redeem coupon, All coupons have been sold out.',
						type: 'error'
					})
				);
			} else {
				dispatch(actions.couponRedeemed({coupon}));
				dispatch(uiActions.uiModal('redeem-coupon'));
			}
		})
		.catch(handleError(dispatch));
};

/**
 * handler function to edit coupon
 * @param {*} coupon
 * @returns
 */
export const editCoupon = (coupon, section) => (dispatch, getState) => {
	dispatch(actions.couponEditing({coupon, section}));
	dispatch(dispatch(uiActions.uiModal('edit-coupon')));
};

export default {
	...actions,
	persistExistingCoupon,
	fetchApprovedCoupons,
	fetchCouponsPendingApproval,
	rejectCoupon,
	approveCoupon,
	deleteCoupon,
	redeemCoupon,
	editCoupon,
	persistEditedCoupon
};



// WEBPACK FOOTER //
// ./src/community/coupons/coupons.actions.js